import { PREDEFINED_TASKS, type PredefinedTask } from "@/components/ai/ai-constants";
import type { PromptInputSubmission, SelectedContext } from "@/components/ai/prompt-input/NewPromptInput";
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import type { PlainProject } from "@/queries/project-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { clearPendingSubmissionFromLocalStorage } from "@/routes/ona-onboarding/local-storage";
import { atom, useAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomFamily, atomWithLazy } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";
import { useDebouncedCallback } from "use-debounce";

export const DROPDOWN_MENU_KIND = {
    none: "none",
    context: "context",
    contextAssociatedProjectSelector: "contextAssociatedProjectSelector",
    projectSelector: "projectSelector",
    urlSelector: "repoSelector",
    environmentClassSelector: "environmentClassSelector",
    /**
     * @deprecated this was used in the old onboarding flow to display the repository browser, but this is no longer used and should be removed.
     */
    repositoryBrowser: "repositoryBrowser",
} as const;
export type DropdownMenuKind = (typeof DROPDOWN_MENU_KIND)[keyof typeof DROPDOWN_MENU_KIND];

const PROMPT_LOCAL_STORAGE_KEY = "conversation-input-home";

export const promptTextareaHeightAtom = atom(0);

export const selectedTasksAtom = atom<PredefinedTask[]>([...PREDEFINED_TASKS]);

export const dropdownMenuKindAtom = atom<DropdownMenuKind>(DROPDOWN_MENU_KIND.none);
export const dropdownMenuWidthAtom = atom(0);

// stores all projects found for a specific context url
export const contextAssociatedProjectsAtom = atom<PlainProject[]>([]);
export const selectedContextsAtom = atom<SelectedContext[]>([]);
export const projectContextAtom = atom((get) =>
    get(selectedContextsAtom).find((context) => context.kind === "project"),
);
export const selectedEnvironmentClassAtom = atom<EnvironmentClassEntry | null>(null);
export const isAwaitingGitHubAuthAtom = atom(false);

export const promptAtom = atomWithLazy(() => {
    if (typeof window !== "undefined") {
        const storedValue = localStorage.getItem(PROMPT_LOCAL_STORAGE_KEY);
        return storedValue !== null ? storedValue : "";
    }
    return "";
});
export const isLoadingAtom = atom(false);

export const promptAtomFamily = atomFamily((id: string) =>
    atomWithLazy(() => {
        if (typeof window !== "undefined") {
            const storedValue = localStorage.getItem(`${PROMPT_LOCAL_STORAGE_KEY}_${id}`);
            return storedValue !== null ? storedValue : "";
        }
        return "";
    }),
);

/**
 * Returns the prompt atom that stores the prompt in the prompt input, scoped to the current user's organization.
 */
export const usePromptAtom = () => {
    // internally, this hook creates a proxy atom per atom created by promptAtomFamily
    //
    // this is necessary because on prompt submission, the prompt text is cleared and then the user is immediately navigated away.
    // this means that the debounced caching will be canceled.
    // this atom provides a synchronous mechanism to clear the prompt text AND the associated local storage key.
    // its not ergonomic to enforce local storage clearance at call site, and imo this is the cleanest way to handle it.

    const { data: user } = useAuthenticatedUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const organizationId = user.organizationId;
    const _promptAtom = promptAtomFamily(organizationId);

    useEffect(
        () => () => {
            // unmount the current prompt atom when the org user changes
            // prompt is loaded from local storage to atom when initializing the prompt atom
            // if the prompt stays mounted, the initialization is only run once, and so the prompt won't be restored properly when switching between organizations
            promptAtomFamily.remove(organizationId);
        },
        [organizationId],
    );

    return useMemo(
        () =>
            atom(
                (get) => get(_promptAtom),
                (_get, set, value: string) => {
                    set(_promptAtom, value);
                    if (!value) {
                        localStorage.removeItem(`${PROMPT_LOCAL_STORAGE_KEY}_${organizationId}`);
                    }
                },
            ),
        [_promptAtom, organizationId],
    );
};

export const useAutoSavePrompt = () => {
    const { data: user } = useAuthenticatedUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const promptAtom = promptAtomFamily(user.organizationId);

    const effectFn: Parameters<typeof atomEffect>[0] = useCallback(
        (get) => {
            const key = `${PROMPT_LOCAL_STORAGE_KEY}_${user.organizationId}`;
            const prompt = get(promptAtom);
            if (prompt) {
                localStorage.setItem(key, prompt);
            } else {
                localStorage.removeItem(key);
            }
        },
        [promptAtom, user],
    );
    const debounced = useDebouncedCallback(effectFn, 500);
    const autoSaveEffect = useMemo(() => atomEffect(debounced), [debounced]);

    useAtom(autoSaveEffect);
};

export const pendingSubmissionAtomFamily = atomFamily((promptAtom: ReturnType<typeof usePromptAtom>) => {
    const a = atom(null, (_get, set, pendingSubmission: PromptInputSubmission | null) => {
        set(a, pendingSubmission);
        if (pendingSubmission) {
            set(promptAtom, pendingSubmission.prompts[0]);
            set(selectedContextsAtom, pendingSubmission.contexts);
            set(selectedEnvironmentClassAtom, pendingSubmission.environmentClass);
        } else {
            set(promptAtom, "");
            set(selectedContextsAtom, []);
            set(selectedEnvironmentClassAtom, null);
        }
    });
    return a;
});

export const usePendingSubmission = () => {
    const { data: user } = useAuthenticatedUser();
    const promptAtom = usePromptAtom();
    const clearPendingSubmission = useCallback(() => {
        if (user?.organizationId) {
            clearPendingSubmissionFromLocalStorage(user.organizationId);
        }
        pendingSubmissionAtomFamily.remove(promptAtom);
    }, [user, promptAtom]);
    return {
        pendingSubmissionAtom: pendingSubmissionAtomFamily(promptAtom),
        clearPendingSubmission,
    };
};
